import type { Lorebook } from "@next-platform/packages-ts-types";

type PersistEditedGroupChatResult = {
  // eslint-disable-next-line id-denylist -- `data` matches the character update API response field
  data: { id?: string } | null;
  error: string | null;
  errorPayload?: {
    // eslint-disable-next-line id-denylist -- `data` matches the character update API error payload
    data?: { submitForManualReview?: boolean };
  };
};

type PersistEditedGroupChatParams<RequestProps> = {
  groupId: string;
  hasGroupContentChanged: boolean;
  requestProps: RequestProps;
  currentLorebooks: Lorebook[];
  initialLorebooks: Lorebook[];
  updateGroup: (groupId: string, requestProps: RequestProps) => Promise<PersistEditedGroupChatResult>;
  syncLorebooks: (currentLorebooks: Lorebook[], initialLorebooks: Lorebook[]) => Promise<{ error: unknown }>;
};

const toErrorMessage = (value: unknown): string | null => {
  if (typeof value === "string") {
    return value;
  }
  if (value == null) {
    return null;
  }
  return String(value);
};

const persistEditedGroupChat = async <RequestProps>({
  groupId,
  hasGroupContentChanged,
  requestProps,
  currentLorebooks,
  initialLorebooks,
  updateGroup,
  syncLorebooks,
}: PersistEditedGroupChatParams<RequestProps>): Promise<PersistEditedGroupChatResult> => {
  if (hasGroupContentChanged) {
    const updateResult = await updateGroup(groupId, requestProps);
    if (updateResult.error || !updateResult["data"]?.id) {
      return updateResult;
    }

    const lorebookResult = await syncLorebooks(currentLorebooks, initialLorebooks);
    if (lorebookResult.error) {
      return {
        ["data"]: updateResult["data"],
        error: toErrorMessage(lorebookResult.error),
        errorPayload: updateResult.errorPayload,
      };
    }

    return updateResult;
  }

  const lorebookResult = await syncLorebooks(currentLorebooks, initialLorebooks);
  return {
    ["data"]: { id: groupId },
    error: toErrorMessage(lorebookResult.error),
  };
};

export default persistEditedGroupChat;
